import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import type { DiscountItemMode } from '@/lib/api/discount-types'
import type { MenuItem } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { FieldError, RuleInput, Section } from './editor-fields'
import {
  EMPTY_RULE,
  formatMenuPrice,
  itemLocationId,
  toggleId,
  type DraftUpdate,
  type ItemDraft,
  type MenuUnits,
  type ScheduleDraft,
} from './editor-model'
import { HINT, LABEL, fieldClass, segmentClass } from './editor-styles'

export interface PickerOption {
  id: number
  label: string
}

type TargetUpdate = DraftUpdate<'vendors' | 'locations' | 'items'>

/** Item search results shown at once; a search narrows a large menu. */
const MAX_ITEMS_SHOWN = 150

const MODES: { value: DiscountItemMode; label: string; help: string }[] = [
  { value: 'include', label: 'Include', help: 'Takes the default rule.' },
  { value: 'override', label: 'Own rule', help: 'Takes its own rule instead of the default.' },
  { value: 'exclude', label: 'Exclude', help: 'Left out even when a chosen vendor or location covers it.' },
]

function MultiPicker({ id, label, hint, options, selected, onToggle, loading, fallbackLabel, emptyText }: {
  id: string
  label: string
  hint: string
  options: PickerOption[]
  selected: number[]
  onToggle: (id: number) => void
  loading: boolean
  fallbackLabel: (id: number) => string
  emptyText: string
}) {
  const [query, setQuery] = useState('')
  const chosen = new Set(selected)
  const labels = new Map(options.map((option) => [option.id, option.label]))
  const needle = query.trim().toLowerCase()
  const shown = needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={`${id}-search`} className={LABEL}>{label}</label>
        <span className={HINT}>{selected.length ? `${selected.length} chosen` : 'None chosen'}</span>
      </div>
      <p className={cn(HINT, 'mt-0.5')}>{hint}</p>
      {selected.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((value) => {
            const text = labels.get(value) ?? fallbackLabel(value)
            return (
              <li key={value} className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 py-1 pl-2.5 pr-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                <span className="truncate">{text}</span>
                <button
                  type="button"
                  onClick={() => onToggle(value)}
                  aria-label={`Remove ${text}`}
                  className="rounded-full p-0.5 hover:bg-blue-100 disabled:hidden dark:hover:bg-blue-800/50"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
      <div className="relative mt-2">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          id={`${id}-search`}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          autoComplete="off"
          className={fieldClass(false, 'w-full pl-8')}
        />
      </div>
      <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-neutral-200 dark:border-neutral-800">
        {loading ? (
          <p className="px-3 py-2 text-sm text-neutral-500">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="px-3 py-2 text-sm text-neutral-500">{options.length ? 'No match.' : emptyText}</p>
        ) : (
          shown.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-neutral-800 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-800/60"
            >
              <input type="checkbox" checked={chosen.has(option.id)} onChange={() => onToggle(option.id)} className="h-4 w-4 shrink-0" />
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          ))
        )}
      </div>
    </div>
  )
}

function ItemPicker({ items, locationLabels, chosen, onToggle, loading, units, emptyText }: {
  items: MenuItem[]
  locationLabels: Map<number, string>
  chosen: Set<number>
  onToggle: (itemId: number) => void
  loading: boolean
  units: MenuUnits
  emptyText: string
}) {
  const [query, setQuery] = useState('')
  const { groups, matched, shown } = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const byLocation = new Map<number, { label: string; items: MenuItem[] }>()
    let matchedCount = 0
    for (const item of items) {
      const location = itemLocationId(item) ?? 0
      const label = locationLabels.get(location) ?? `Location ${location}`
      if (needle && !item.name.toLowerCase().includes(needle) && !label.toLowerCase().includes(needle)) continue
      matchedCount += 1
      const group = byLocation.get(location) ?? { label, items: [] }
      group.items.push(item)
      byLocation.set(location, group)
    }
    const sorted = [...byLocation.entries()].sort(([, a], [, b]) => a.label.localeCompare(b.label))
    let budget = MAX_ITEMS_SHOWN
    const visible: { location: number; label: string; items: MenuItem[] }[] = []
    for (const [location, group] of sorted) {
      if (budget <= 0) break
      const groupItems = [...group.items].sort((a, b) => a.name.localeCompare(b.name)).slice(0, budget)
      budget -= groupItems.length
      visible.push({ location, label: group.label, items: groupItems })
    }
    return { groups: visible, matched: matchedCount, shown: MAX_ITEMS_SHOWN - budget }
  }, [items, locationLabels, query])

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          id="discount-item-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search items, vendors or booths"
          aria-label="Search items"
          autoComplete="off"
          className={fieldClass(false, 'w-full pl-8')}
        />
      </div>
      <div className="mt-1 max-h-64 overflow-y-auto rounded-md border border-neutral-200 dark:border-neutral-800">
        {loading ? (
          <p className="px-3 py-2 text-sm text-neutral-500">Loading items…</p>
        ) : groups.length === 0 ? (
          <p className="px-3 py-2 text-sm text-neutral-500">{items.length ? 'No match.' : emptyText}</p>
        ) : (
          groups.map((group) => (
            <div key={group.location}>
              <div className="sticky top-0 z-10 border-b border-neutral-200 bg-neutral-50 px-3 py-1 text-xs font-semibold text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
                {group.label}
              </div>
              {group.items.map((item) => (
                <label
                  key={item.id}
                  className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-neutral-800 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-800/60"
                >
                  <input type="checkbox" checked={chosen.has(item.id)} onChange={() => onToggle(item.id)} className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  <span className="shrink-0 tabular-nums text-neutral-500 dark:text-neutral-400">{formatMenuPrice(item.price, units)}</span>
                </label>
              ))}
            </div>
          ))
        )}
      </div>
      {!loading && matched > shown && (
        <p className={cn(HINT, 'mt-1')}>Showing {shown} of {matched} items. Search to narrow the list.</p>
      )}
    </div>
  )
}

function ChosenItem({ row, item, locationLabel, covered, units, error, onChange, onRemove }: {
  row: ItemDraft
  item: MenuItem | undefined
  locationLabel: string | null
  /** A chosen vendor or location already covers the item. */
  covered: boolean
  units: MenuUnits
  error?: string
  onChange: (row: ItemDraft) => void
  onRemove: () => void
}) {
  const name = item?.name ?? `Item ${row.item}`
  const setMode = (mode: DiscountItemMode) => {
    if (mode === row.mode) return
    // An own rule starts as a set price, the usual per-item deal; the other
    // modes carry no rule.
    const rule = mode === 'override' ? (row.rule.type ? row.rule : { type: 'set_price' as const, value: '' }) : row.rule
    onChange({ ...row, mode, rule })
  }

  return (
    <li className="space-y-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">{name}</p>
          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {[locationLabel, item ? `List ${formatMenuPrice(item.price, units)}` : 'Not in the loaded menu'].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div role="group" aria-label={`How ${name} is treated`} className="inline-flex">
            {MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                title={mode.help}
                aria-pressed={row.mode === mode.value}
                onClick={() => setMode(mode.value)}
                className={segmentClass(row.mode === mode.value)}
              >
                {mode.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${name}`}
            className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:hidden dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {row.mode === 'override' && (
        <RuleInput
          id={`discount-item-${row.item}-rule`}
          rule={row.rule}
          onChange={(rule) => onChange({ ...row, rule })}
          units={units}
          error={error}
          compact
          subject={name}
        />
      )}
      {row.mode !== 'override' && error && <FieldError>{error}</FieldError>}
      {row.mode === 'exclude' && !covered && (
        <p className={HINT}>No chosen vendor or location covers this item, so excluding it changes nothing.</p>
      )}
    </li>
  )
}

export function TargetingSection({
  draft, onChange, vendorOptions, locationOptions, vendorLabels, locationLabels, vendorOfLocation, items, offeredItems, scoped,
  itemsLoading, locationsLoading, units, targetsError, itemErrors,
}: {
  draft: ScheduleDraft
  onChange: (update: TargetUpdate) => void
  /** The vendors and locations that may be added (the viewer's scope when not event-wide). */
  vendorOptions: PickerOption[]
  locationOptions: PickerOption[]
  /** Vendor id → name, for every vendor at the event (chosen ones may be outside the options). */
  vendorLabels: Map<number, string>
  /** Location id → "Vendor · booth", for every location at the event. */
  locationLabels: Map<number, string>
  /** Location id → vendor id. */
  vendorOfLocation: Map<number, number>
  /** Every item at the event, to describe the chosen ones. */
  items: MenuItem[]
  /** The items that may be added. */
  offeredItems: MenuItem[]
  /** The options are narrowed to what the viewer may discount. */
  scoped: boolean
  itemsLoading: boolean
  locationsLoading: boolean
  units: MenuUnits
  targetsError?: string | null
  itemErrors: Record<number, string>
}) {
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])
  const chosenItems = new Set(draft.items.map((row) => row.item))

  const toggleItem = (itemId: number) =>
    onChange((current) => ({
      items: current.items.some((row) => row.item === itemId)
        ? current.items.filter((row) => row.item !== itemId)
        : [...current.items, { item: itemId, mode: 'include', rule: EMPTY_RULE }],
    }))
  const updateRow = (next: ItemDraft) =>
    onChange((current) => ({ items: current.items.map((row) => (row.item === next.item ? next : row)) }))
  const removeRow = (itemId: number) => onChange((current) => ({ items: current.items.filter((row) => row.item !== itemId) }))
  const covered = (item: MenuItem | undefined) => {
    if (!item) return false
    const location = itemLocationId(item)
    if (location == null) return false
    const vendor = vendorOfLocation.get(location)
    return draft.locations.includes(location) || (vendor !== undefined && draft.vendors.includes(vendor))
  }

  return (
    <Section
      title="What it discounts"
      description="Every item of a chosen vendor or location, plus the items you add. An added item takes the default rule, its own rule, or is left out."
    >
      {targetsError && <FieldError>{targetsError}</FieldError>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <MultiPicker
          id="discount-vendors"
          label="Vendors"
          hint="All of the vendor's locations at this event."
          options={vendorOptions}
          selected={draft.vendors}
          onToggle={(vendor) => onChange((current) => ({ vendors: toggleId(current.vendors, vendor) }))}
          loading={locationsLoading}
          fallbackLabel={(id) => vendorLabels.get(id) ?? `Vendor ${id}`}
          emptyText={scoped ? 'No vendors you can discount at this event.' : 'No vendors at this event.'}
        />
        <MultiPicker
          id="discount-locations"
          label="Locations"
          hint="Every item at the booth."
          options={locationOptions}
          selected={draft.locations}
          onToggle={(location) => onChange((current) => ({ locations: toggleId(current.locations, location) }))}
          loading={locationsLoading}
          fallbackLabel={(id) => locationLabels.get(id) ?? `Location ${id}`}
          emptyText={scoped ? 'No locations you can discount at this event.' : 'No locations at this event.'}
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between gap-2">
          <span className={LABEL}>Items</span>
          <span className={HINT}>{draft.items.length ? `${draft.items.length} added` : 'None added'}</span>
        </div>
        <p className={cn(HINT, 'mb-2 mt-0.5')}>Prices are list prices, before any sale or schedule.</p>
        <ItemPicker
          items={offeredItems}
          locationLabels={locationLabels}
          chosen={chosenItems}
          onToggle={toggleItem}
          loading={itemsLoading}
          units={units}
          emptyText={scoped ? 'No menu items you can discount at this event.' : 'This event has no menu items.'}
        />
      </div>

      {draft.items.length > 0 && (
        <ul className="space-y-2">
          {draft.items.map((row) => {
            const item = itemsById.get(row.item)
            const location = item ? itemLocationId(item) : null
            return (
              <ChosenItem
                key={row.item}
                row={row}
                item={item}
                locationLabel={location == null ? null : locationLabels.get(location) ?? null}
                covered={covered(item)}
                units={units}
                error={itemErrors[row.item]}
                onChange={updateRow}
                onRemove={() => removeRow(row.item)}
              />
            )
          })}
        </ul>
      )}
    </Section>
  )
}
